import React, { useEffect, useState, useRef } from "react";
import { Slider } from "antd";

const RangeSlider = ({ min, max, defaultValue, onSlide, count, sliderType, isRange = true, ranges }) => {
	// If ranges are provided, override the slider's domain using the provided ranges.
	// The slider's min will be the first range's lower bound,
	// and the slider's max will be the last range's upper bound.
	const hasCustomRanges = ranges && Array.isArray(ranges) && ranges.length > 0;
	const sliderMin = hasCustomRanges ? ranges[0].value[0] : min;
	const sliderMax = hasCustomRanges ? ranges[ranges.length - 1].value[1] : max;

	// Determine the initial value.
	// For a range slider, use the provided defaultValue (or fallback to [sliderMin, sliderMax]).
	// For a single–value slider, if defaultValue is an array, take its first element.
	const initialValue = isRange
		? defaultValue || [sliderMin, sliderMax]
		: Array.isArray(defaultValue)
		? defaultValue[0]
		: defaultValue !== undefined
		? defaultValue
		: sliderMin;
	const prevMinMax = useRef({ min, max });
	const [selectedValue, setSelectedValue] = useState(initialValue);

	useEffect(() => {
		// Compare current values to previous ones
		if (prevMinMax.current.min !== min || prevMinMax.current.max !== max) {
			if (min && max) {
				setSelectedValue([min, max]);
			}
			// Update the ref with the new values
			prevMinMax.current = { min, max };
		}
	}, [min, max]);

	const getMarks = () => {
		// If custom ranges are provided, generate marks only from them.
		if (hasCustomRanges) {
			const marks = {};
			const count = ranges.length;

			// Use linear interpolation so that:
			// - The first mark is at 0.
			// - The last mark is at max.
			// - The ones in between are equally spaced.
			ranges.forEach((range, index) => {
				// Calculate the mark key as a linear interpolation between 0 and max.
				const key = Math.round(max * (index / (count - 1)));
				marks[key] = {
					style: { color: "white" },
					label: <strong style={{ fontSize: 10 }}>{range.text}</strong>
				};
			});
			return marks;
		}

		// Otherwise, generate marks based on the sliderType.
		let marks = {};
		if (sliderType === "hourly") {
			const hours = [
				"12 AM",
				"1 AM",
				"2 AM",
				"3 AM",
				"4 AM",
				"5 AM",
				"6 AM",
				"7 AM",
				"8 AM",
				"9 AM",
				"10 AM",
				"11 AM",
				"12 PM",
				"1 PM",
				"2 PM",
				"3 PM",
				"4 PM",
				"5 PM",
				"6 PM",
				"7 PM",
				"8 PM",
				"9 PM",
				"10 PM",
				"11 PM"
			];
			for (let i = min; i <= max; i++) {
				marks[i] = {
					style: { color: "white" },
					label: <strong style={{ fontSize: 10 }}>{hours[i % 24]}</strong>
				};
			}
		} else if (sliderType === "weekly") {
			const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
			const delta = (max - min) / (days.length - 1);
			for (let i = 0; i < days.length; i++) {
				const value = min + delta * i;
				marks[Math.round(value)] = {
					style: { color: "white" },
					label: <strong style={{ fontSize: 10 }}>{days[i]}</strong>
				};
			}
		} else if (["traffic", "demographic"].includes(sliderType)) {
			marks = {
				[min]: { style: { color: "white" }, label: <strong style={{ fontSize: 10 }}>Low</strong> },
				[Math.round((min + max) / 2)]: {
					style: { color: "white" },
					label: <strong style={{ fontSize: 10 }}>Med</strong>
				},
				[max]: { style: { color: "white" }, label: <strong style={{ fontSize: 10 }}>High</strong> }
			};
		} else {
			const delta = (max - min) / count;
			for (let i = 0; i <= count; i++) {
				const value = min + delta * i;
				marks[parseFloat(value.toFixed(0))] = {
					style: { color: "white" },
					label: <strong style={{ fontSize: 10 }}>{value.toFixed(0)}</strong>
				};
			}
		}
		return marks;
	};

	const convertSliderValueToRangeValue = sliderValue => {
		const numRanges = ranges.length;
		const keys = ranges.map((_, index) =>
			Math.round(sliderMin + (index / (numRanges - 1)) * (sliderMax - sliderMin))
		);
		if (Array.isArray(sliderValue)) {
			return sliderValue.map(val => {
				const idx = keys.findIndex(key => key === val);
				return idx !== -1 ? ranges[idx].value : val;
			});
		} else {
			const idx = keys.findIndex(key => key === sliderValue);
			return idx !== -1 ? ranges[idx].value : sliderValue;
		}
	};

	const handleChange = value => {
		setSelectedValue(value);
		if (hasCustomRanges) {
			const convertedValue = convertSliderValueToRangeValue(value);
			onSlide(convertedValue);
		} else {
			onSlide(value);
		}
	};

	return (
		<Slider
			value={selectedValue}
			range={isRange}
			// If custom ranges exist, use sliderMin/sliderMax; otherwise, use the provided min/max.
			min={hasCustomRanges ? sliderMin : min}
			max={hasCustomRanges ? sliderMax : max}
			defaultValue={initialValue}
			onChange={handleChange}
			marks={getMarks()}
			step={null} // Snap to provided marks only.
			trackStyle={!isRange ? { backgroundColor: "transparent" } : undefined}
			railStyle={{ backgroundColor: "#d9d9d9" }}
		/>
	);
};

export default RangeSlider;
